import { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';

const CHART_H = 220;

const tooltipStyle = {
  contentStyle: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 },
  labelStyle: { color: 'var(--text-secondary)', marginBottom: 4 },
  itemStyle: { fontSize: 12 },
};

const axisProps = {
  tick: { fill: 'var(--text-muted)', fontSize: 11 },
  axisLine: { stroke: 'var(--border)' },
  tickLine: false,
};

const CHART_INFO = {
  'Margin Trends': {
    headline: 'Margin Trends — Gross, Operating & Net',
    body: [
      { label: 'Gross Margin', text: 'Revenue minus Cost of Goods Sold, divided by Revenue. Shows how efficiently a company produces its goods. A consistently high or rising gross margin indicates pricing power and competitive advantage.' },
      { label: 'Operating Margin', text: 'Profit after paying for production AND operating expenses (salaries, rent, R&D). This reveals how well management controls costs. Industry leaders typically have operating margins well above peers.' },
      { label: 'Net Margin', text: 'The bottom-line profit after ALL expenses including taxes and interest. This is the % of every dollar of revenue that becomes profit.' },
      { label: 'What to look for', text: 'Higher is better. Rising margins over time signal operational leverage — the company earns more profit per dollar of sales as it scales. Declining margins are a red flag, especially if revenue is growing.' },
    ],
  },
  'Profitability Returns': {
    headline: 'Return Metrics — ROE, ROIC & ROA',
    body: [
      { label: 'ROE (Return on Equity)', text: 'Net Income ÷ Shareholders Equity. Measures how efficiently management uses shareholder capital to generate profit. Above 15% is generally considered strong. Very high ROE (>50%) may indicate debt leverage rather than genuine efficiency — always check alongside debt levels.' },
      { label: 'ROIC (Return on Invested Capital)', text: 'Net Income ÷ (Equity + Debt). The gold standard metric for capital efficiency — it shows how well the company deploys ALL its capital (debt + equity). A company creating value must have ROIC > its cost of capital (WACC). Warren Buffett and other value investors emphasize this metric heavily.' },
      { label: 'ROA (Return on Assets)', text: 'Net Income ÷ Total Assets. Measures how profitably a company uses its assets. Asset-light businesses (tech, software) have high ROA; capital-intensive businesses (manufacturing, utilities) have lower ROA by nature.' },
      { label: 'What to look for', text: 'Higher is better. Consistently rising returns signal a strengthening competitive moat. Compare against the same industry — a 10% ROE might be excellent in banking but mediocre in software.' },
    ],
  },
  'Cash Conversion Cycle': {
    headline: 'Cash Conversion Cycle (CCC)',
    body: [
      { label: 'Formula', text: 'CCC = Days Inventory Outstanding (DIO) + Days Sales Outstanding (DSO) − Days Payable Outstanding (DPO)' },
      { label: 'DIO', text: 'How many days inventory sits on the shelf before being sold. Lower is better.' },
      { label: 'DSO', text: 'How many days it takes to collect payment from customers after a sale. Lower is better.' },
      { label: 'DPO', text: 'How many days the company takes to pay its own suppliers. Higher is better — it means the company holds cash longer.' },
      { label: 'Interpretation', text: 'A LOWER (or negative) CCC is better. A negative CCC (like Amazon or Apple) means the company collects cash from customers BEFORE it pays suppliers — essentially using supplier money to fund operations. This is a sign of exceptional bargaining power. A very high positive CCC means capital is tied up for a long time, which strains cash flow.' },
      { label: 'Service companies', text: 'Companies with no inventory (software, advertising) have DIO = 0. Their CCC is purely DSO − DPO. A negative CCC is still excellent for them.' },
    ],
  },
  'Revenue vs Net Receivables': {
    headline: 'Revenue vs Net Accounts Receivable',
    body: [
      { label: 'What it shows', text: 'Revenue (blue) is the total sales in billions. Net Receivables (pink) are amounts owed to the company by customers — money earned but not yet collected.' },
      { label: 'Healthy pattern', text: 'Receivables should grow in proportion to (or slower than) revenue. If both lines rise together at similar rates, the business is scaling normally.' },
      { label: 'Red flag', text: 'If receivables grow FASTER than revenue year over year, it may signal: (1) customers are struggling to pay, (2) the company is booking "channel stuffing" — pushing excess inventory to distributors that may be returned, or (3) aggressive revenue recognition. This was a warning sign in major accounting frauds.' },
      { label: 'What to look for', text: 'The revenue line should always be well above the receivables line. A widening gap (revenue growing faster) is a positive sign of cash conversion efficiency.' },
    ],
  },
  'Cash & ST Investments vs Total Debt': {
    headline: 'Cash & Short-Term Investments vs Total Debt',
    body: [
      { label: 'Cash & ST Investments', text: 'Total liquid assets — cash on hand plus short-term investments that can be converted quickly. This is the company\'s financial cushion for downturns, acquisitions, or buybacks.' },
      { label: 'Total Debt', text: 'All interest-bearing liabilities — short-term + long-term debt. This represents obligations the company must repay.' },
      { label: 'Net Cash Position', text: 'Cash minus Debt = Net Cash. When the green (cash) line is ABOVE the red (debt) line, the company has a net cash position — it could theoretically pay off all debt immediately. This is a very strong balance sheet indicator. Companies like Apple and Berkshire Hathaway maintain massive net cash positions.' },
      { label: 'What to look for', text: 'Ideally cash grows over time while debt stays flat or falls. Rising debt with falling cash is a warning sign. A company with net debt is not necessarily bad — leverage can amplify returns — but it adds risk, especially in downturns.' },
    ],
  },
  'Shares Outstanding': {
    headline: 'Shares Outstanding Over Time',
    body: [
      { label: 'What it shows', text: 'The total number of company shares in existence each year (in millions). This changes through share buybacks, new share issuance, stock splits, or employee stock option exercises.' },
      { label: 'Declining shares = Buybacks', text: 'When the count falls year over year, the company is buying back its own shares — reducing the share count. This increases Earnings Per Share (EPS) and ownership % for existing shareholders. Apple has reduced its share count by over 40% over the past decade through buybacks. This is widely viewed as shareholder-friendly.' },
      { label: 'Rising shares = Dilution', text: 'When the count increases, the company is issuing new shares — often to fund growth, pay employees via stock options, or raise capital. This dilutes existing shareholders. High share issuance in unprofitable growth companies is a red flag if it outpaces revenue growth.' },
      { label: 'What to look for', text: 'For mature, profitable companies: declining share count is excellent. For early-stage growth companies: modest increases may be acceptable if the capital is deployed effectively. Watch for large sudden spikes — these often indicate equity raises at unfavorable terms.' },
    ],
  },
};

function InfoModal({ info, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div ref={ref} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 28, maxWidth: 560, width: '100%',
        maxHeight: '80vh', overflowY: 'auto',
        scrollbarWidth: 'none',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', flex: 1, paddingRight: 12 }}>{info.headline}</div>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 6,
            color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 8px', fontSize: 16,
            flexShrink: 0,
          }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {info.body.map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  const [open, setOpen] = useState(false);
  const info = CHART_INFO[title];
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div className="card-title" style={{ margin: 0 }}>{title}</div>
        {info && (
          <button
            onClick={() => setOpen(true)}
            title="Learn more"
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: '50%',
              width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 11, fontWeight: 700,
              color: 'var(--text-muted)', flexShrink: 0, lineHeight: 1,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
          >i</button>
        )}
      </div>
      {children}
      {open && info && <InfoModal info={info} onClose={() => setOpen(false)} />}
    </div>
  );
}

function MarginChart({ data, chartType }) {
  const hasData = data.some(d => d.grossMargin != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No margin data available</div>;

  const common = { dataKey: 'year', ...axisProps };
  const ChartComp = chartType === 'bar' ? BarChart : LineChart;
  const Series = chartType === 'bar' ? Bar : Line;
  const lineProps = chartType === 'line' ? { type: 'monotone', dot: { r: 3 }, strokeWidth: 2 } : {};

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <ChartComp data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={v => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v}%`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Series dataKey="grossMargin" name="Gross Margin" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.7} {...lineProps} />
        <Series dataKey="operatingMargin" name="Op. Margin" stroke="#f97316" fill="#f97316" fillOpacity={0.7} {...lineProps} />
        <Series dataKey="netMargin" name="Net Margin" stroke="#22c55e" fill="#22c55e" fillOpacity={0.7} {...lineProps} />
      </ChartComp>
    </ResponsiveContainer>
  );
}

function ReturnChart({ data, chartType }) {
  const hasData = data.some(d => d.roe != null || d.roa != null || d.roic != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No return data available</div>;

  const ChartComp = chartType === 'bar' ? BarChart : LineChart;
  const Series = chartType === 'bar' ? Bar : Line;
  const lineProps = chartType === 'line' ? { type: 'monotone', dot: { r: 3 }, strokeWidth: 2 } : {};

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <ChartComp data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={v => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v != null ? v + '%' : '—'}`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Series dataKey="roe" name="ROE" stroke="#a855f7" fill="#a855f7" fillOpacity={0.7} {...lineProps} />
        <Series dataKey="roic" name="ROIC" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.7} {...lineProps} />
        <Series dataKey="roa" name="ROA" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.7} {...lineProps} />
      </ChartComp>
    </ResponsiveContainer>
  );
}

function RevenueARChart({ data, chartType }) {
  const hasData = data.some(d => d.revenue != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No revenue data available</div>;

  const ChartComp = chartType === 'bar' ? BarChart : LineChart;
  const RevSeries = chartType === 'bar' ? Bar : Line;
  const lineProps = chartType === 'line' ? { type: 'monotone', dot: { r: 3 }, strokeWidth: 2 } : {};

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <ChartComp data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis yAxisId="left" {...axisProps} tickFormatter={v => `$${v}B`} />
        <YAxis yAxisId="right" orientation="right" {...axisProps} tickFormatter={v => `$${v}M`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [name === 'Revenue' ? `$${v}B` : `$${v}M`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <RevSeries yAxisId="left" dataKey="revenue" name="Revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.7} {...lineProps} />
        <RevSeries yAxisId="right" dataKey="ar" name="Net Receivables" stroke="#ec4899" fill="#ec4899" fillOpacity={0.7} {...lineProps} />
      </ChartComp>
    </ResponsiveContainer>
  );
}

function CashDebtChart({ data, chartType }) {
  const hasData = data.some(d => d.cash != null || d.totalDebt != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No cash / debt data available</div>;

  const ChartComp = chartType === 'bar' ? BarChart : LineChart;
  const Series = chartType === 'bar' ? Bar : Line;
  const lineProps = chartType === 'line' ? { type: 'monotone', dot: { r: 3 }, strokeWidth: 2 } : {};

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <ChartComp data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={v => `$${v}B`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [v != null ? `$${v}B` : '—', name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Series dataKey="cash" name="Cash & ST Investments" stroke="#22c55e" fill="#22c55e" fillOpacity={0.7} {...lineProps} />
        <Series dataKey="totalDebt" name="Total Debt" stroke="#ef4444" fill="#ef4444" fillOpacity={0.7} {...lineProps} />
      </ChartComp>
    </ResponsiveContainer>
  );
}

function SharesChart({ data }) {
  const hasData = data.some(d => d.sharesOutstanding != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No shares data available</div>;

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={v => `${v}M`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [v != null ? `${v}M shares` : '—', name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="sharesOutstanding" name="Shares Outstanding" stroke="#a3e635" dot={{ r: 3 }} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function CCCChart({ data }) {
  const hasData = data.some(d => d.ccc != null);
  if (!hasData) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 40 }}>No cash cycle data (requires inventory &amp; payables)</div>;

  return (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
        <XAxis dataKey="year" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={v => `${v}d`} />
        <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v} days`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="ccc" name="Cash Conversion Cycle" stroke="#a855f7" dot={{ r: 4 }} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function TrendCharts({ fundamentals }) {
  const [chartType, setChartType] = useState('line');
  const data = fundamentals?.trends;

  if (!data || data.length === 0) return null;

  const Toggle = () => (
    <div style={{ display: 'flex', gap: 4, background: 'var(--bg-base)', borderRadius: 6, padding: 3 }}>
      {['line', 'bar'].map(t => (
        <button
          key={t}
          onClick={() => setChartType(t)}
          style={{
            padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
            background: chartType === t ? 'var(--accent)' : 'transparent',
            color: chartType === t ? '#fff' : 'var(--text-muted)',
            transition: 'all 0.15s',
          }}
        >
          {t === 'line' ? 'Line' : 'Bar'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="card-title" style={{ margin: 0 }}>Trend Charts</div>
        <Toggle />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <ChartCard title="Margin Trends">
          <MarginChart data={data} chartType={chartType} />
        </ChartCard>
        <ChartCard title="Profitability Returns">
          <ReturnChart data={data} chartType={chartType} />
        </ChartCard>
        <ChartCard title="Cash Conversion Cycle">
          <CCCChart data={data} />
        </ChartCard>
        <ChartCard title="Revenue vs Net Receivables">
          <RevenueARChart data={data} chartType={chartType} />
        </ChartCard>
        <ChartCard title="Cash & ST Investments vs Total Debt">
          <CashDebtChart data={data} chartType={chartType} />
        </ChartCard>
        <ChartCard title="Shares Outstanding">
          <SharesChart data={data} />
        </ChartCard>
      </div>
    </div>
  );
}
